import { Link } from 'react-router-dom';
import { BellRing, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useActiveWalkIn } from '@/hooks/queries/useActiveWalkIn';
import { useAuth } from '@/hooks/useAuth';

export function CompanyQueueStatus({ companyId, slug }: { companyId: string; slug: string }) {
  const { user, isAuthenticated } = useAuth();
  const query = useActiveWalkIn(isAuthenticated ? user?.id : null);
  if (!isAuthenticated) return null;
  if (query.isPending)
    return (
      <p role="status" className="text-muted-foreground text-sm">
        Consultando sua fila…
      </p>
    );
  if (query.isError)
    return (
      <div role="alert" className="rounded-xl border p-4 text-sm">
        Não foi possível atualizar sua posição na fila.
        <Button variant="link" onClick={() => void query.refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  const entry = query.data;
  if (!entry || entry.companyId !== companyId) return null;
  const called = entry.status === 'CALLED';
  const started = entry.status === 'IN_ATTENDANCE';
  return (
    <section
      id="minha-fila"
      aria-label="Minha fila"
      className="border-primary/30 bg-primary-light scroll-mt-20 rounded-2xl border p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-4" aria-live="polite">
        <div>
          <p className="text-primary text-sm font-semibold">Minha fila</p>
          <h2 className="mt-1 text-xl font-bold">
            {called ? 'Chegou sua vez!' : started ? 'Atendimento em andamento' : 'Seu lugar está reservado'}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">
            {entry.professional?.name ? `Com ${entry.professional.name}` : 'Atendimento presencial'}
          </p>
        </div>
        {!called && !started && (
          <div className="shrink-0 text-center">
            <p className="text-primary text-5xl font-bold">{entry.position != null ? `${entry.position}º` : '—'}</p>
            <p className="text-muted-foreground mt-1 text-xs">na fila</p>
          </div>
        )}
      </div>
      <p className="text-muted-foreground mt-4 text-sm">
        {called
          ? 'Procure o profissional para iniciar o atendimento.'
          : started
            ? 'Seu atendimento já começou.'
            : 'Sua posição é atualizada automaticamente enquanto você acompanha a fila.'}
      </p>
      <Button asChild className="mt-5 h-12 w-full gap-2 rounded-xl sm:w-auto">
        <Link to={`/${slug}/walk-in`}>
          Acompanhar minha fila <ArrowRight className="h-4 w-4" />
        </Link>
      </Button>
      <div className="border-primary/15 mt-5 flex items-start gap-2 border-t pt-4 text-sm">
        <BellRing className="text-primary mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <p className="text-muted-foreground">
          Receba avisos quando estiver próximo e quando chegar sua vez, conforme suas preferências.{' '}
          <Link className="text-primary underline" to="/perfil?tab=preferences">
            Configurar avisos
          </Link>
        </p>
      </div>
    </section>
  );
}
